// @flow

import React, { useRef, type Node } from 'react';
import BoardSortSelectlist from 'app/common/react/components/profile/ProfileHeader/BoardSortSelectlist';
import ExperienceTooltip from 'app/common/react/components/experiences/ExperienceTooltip/ExperienceTooltip';
import i18n from 'app/common/lib/i18n';
import PinRepViewSelector from 'app/common/react/components/save/board/PinRepViewSelector/PinRepViewSelector';
import ProfileBoardViewSelector from 'app/common/react/components/profile/ProfileHeader/ProfileBoardViewSelector';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Tabs } from 'gestalt';
import { ExperimentReader, SingleTreatmentExp } from 'app/common/lib/experiment';

// TODO(Dana): replace with PCONST after adding to thrift
const DISCOVER_TAB_EDUCATION_EXPERIENCE_ID = 501734;

export const ProfileTabs = {
  BOARDS: 'boards',
  ACTIVITIES: '_activities',
  ACTIVITY: '_activity',
  COMMUNITY: '_community',
  DECIDER: 'decider',
  DISCOVER: 'discover',
  FOLLOWERS: '_followers',
  FOLLOWING: '_following',
  OVERVIEW: 'overview',
  PINS: 'pins',
  SHOP: '_shop',
  VIDEO_PINS: 'video_pins',
  TRIED: 'tried',
  TOPICS: 'topics',
  STORY_PINS: 'story_pins',
};

export type ProfileTabType = $Values<typeof ProfileTabs>;

type Props = {|
  onClickTab?: Function,
  requestContext: RequestContextType,
  router?: Router,
  tab: ProfileTabType,
  user: ProfileUser,
  viewer: Viewer,
|};

type Tab = {|
  isActive: boolean,
  onClick?: () => void,
  tabText: Node,
  url: string,
|};

const isModifiedEvent = (event: SyntheticMouseEvent<>) =>
  !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

export function ProfileTabBar(props: Props) {
  const { onClickTab, tab, router, user, viewer, requestContext } = props;

  const isOwnProfile = viewer.isAuth && viewer.username === user.username;
  const isPartner = viewer.isAuth && viewer.isPartner;
  const hasMinimumAmountOfBoards = Math.max(user.secret_board_count, user.board_count) >= 3;

  // Store items to display
  const tabs: Array<Tab> = [];

  const discoverTabRef = useRef(null);

  const boardsTab = {
    isActive: tab === ProfileTabs.BOARDS,
    onClick: onClickTab ? () => onClickTab(ProfileTabs.BOARDS) : undefined,
    tabText: i18n._('Boards'),
    url: `/${user.username}/boards/`,
  };

  const pinsTab = {
    isActive: tab === ProfileTabs.PINS,
    onClick: onClickTab ? () => onClickTab(ProfileTabs.PINS) : undefined,
    tabText: i18n._('Pins'),
    url: `/${user.username}/pins/`,
  };

  const topicTab = {
    isActive: tab === ProfileTabs.TOPICS,
    onClick: onClickTab ? () => onClickTab(ProfileTabs.TOPICS) : undefined,
    tabText: i18n._('Topics', 'label for topics tab on profile'),
    url: `/${user.username}/topics/`,
  };

  const discoverTab = {
    isActive: tab === ProfileTabs.DISCOVER,
    onClick: onClickTab ? () => onClickTab(ProfileTabs.DISCOVER) : undefined,
    tabText: (
      <div ref={discoverTabRef}>{i18n._('Discover', 'label for discover tab on profile')}</div>
    ),
    url: `/${user.username}/discover/`,
  };

  const followersTab = {
    isActive: tab === ProfileTabs.FOLLOWERS,
    onClick: onClickTab ? () => onClickTab(ProfileTabs.FOLLOWERS) : undefined,
    tabText: i18n._('Followers', 'label for followers tab on profile'),
    url: `/${user.username}/_followers/`,
  };

  const followingTab = {
    isActive: tab === ProfileTabs.FOLLOWING,
    onClick: onClickTab ? () => onClickTab(ProfileTabs.FOLLOWING) : undefined,
    tabText: i18n._('Following', 'label for following tab on profile'),
    url: `/${user.username}/_following/`,
  };

  const profileTabExperimentGroup = new ExperimentReader(
    requestContext,
    'web_profile_tabs_parent',
  ).getGroup(/* activate= */ true);

  let profileParityEnabled = false;
  let showDiscoverTab = false;

  if (profileTabExperimentGroup === 'web_all_profiles_follow_parity') {
    profileParityEnabled = new SingleTreatmentExp(
      requestContext,
      'web_all_profiles_follow_parity',
    ).enabled();
  } else if (profileTabExperimentGroup === 'web_profile_discover_tab') {
    showDiscoverTab =
      isOwnProfile && new SingleTreatmentExp(requestContext, 'web_profile_discover_tab').enabled();
  }

  const topicTabCondition =
    !showDiscoverTab &&
    (profileParityEnabled
      ? !isPartner && !viewer.isLimitedLogin
      : isOwnProfile && !isPartner && !viewer.isLimitedLogin);

  if (isOwnProfile && isPartner) {
    tabs.push(pinsTab, boardsTab);
  } else {
    tabs.push(boardsTab, pinsTab);
  }

  if (showDiscoverTab) {
    tabs.push(discoverTab);
  }

  if (!user.blocked_by_me && viewer.isAuth && (user.pins_done_count || isOwnProfile)) {
    tabs.push({
      isActive: tab === ProfileTabs.TRIED,
      onClick: onClickTab ? () => onClickTab(ProfileTabs.TRIED) : undefined,
      tabText: i18n._('Tries', 'label for tries tab on profile'),
      url: `/${user.username}/tried/`,
    });
  }

  if (profileParityEnabled) {
    tabs.push(followersTab, followingTab);
  }

  if (topicTabCondition) {
    tabs.push(topicTab);
  }

  // There should always be a tab with isActive set to true, but just in case
  // the find call fails and returns -1 lets default to 0
  const activeTabIndex = Math.max(tabs.findIndex((tab) => tab.isActive), 0);

  const tabNav = (
    <Box
      display="flex"
      direction="row"
      justifyContent="between"
      alignItems="center"
      marginStart={-1}
      wrap
    >
      <Box marginBottom={1}>
        <Tabs
          activeTabIndex={activeTabIndex}
          onChange={({ event, activeTabIndex }) => {
            const tab = tabs[activeTabIndex];
            if (tab.onClick) {
              tab.onClick();
            }
            // Only change routes if onClick did not prevent default, the click
            // came from the left mouse button, and there were no modifier keys
            if (
              !event.defaultPrevented &&
              event.button === 0 &&
              !isModifiedEvent(event) &&
              router
            ) {
              event.preventDefault();
              router.push(tab.url);
            }
          }}
          tabs={tabs.map((tab) => ({ text: tab.tabText, href: tab.url }))}
        />
        {discoverTabRef && (
          <Box
            dangerouslySetInlineStyle={{
              __style: {
                zIndex: 1,
              },
            }}
          >
            <ExperienceTooltip
              anchor={discoverTabRef.current}
              experienceIds={[DISCOVER_TAB_EDUCATION_EXPERIENCE_ID]}
              placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER}
              useMasonryFlyout
            />
          </Box>
        )}
      </Box>
      {tab === ProfileTabs.PINS &&
        isOwnProfile && (
          <Box marginLeft={-1} marginRight={-1} paddingX={1}>
            <PinRepViewSelector
              viewType={PCONST.thrift.ViewType.USER}
              component={PCONST.thrift.ComponentType.PROFILE_PIN_REP_VIEW_TYPE_TOGGLE}
              pinView="profile"
            />
          </Box>
        )}
      {tab === ProfileTabs.BOARDS &&
        isOwnProfile &&
        hasMinimumAmountOfBoards && (
          <Box display="flex" marginLeft={-1} marginRight={-1}>
            <Box paddingX={1}>
              <ProfileBoardViewSelector />
            </Box>
            <Box paddingX={1}>
              <BoardSortSelectlist
                isOwnProfile={isOwnProfile}
                hasCustomOrder={user.has_custom_board_sorting_order}
              />
            </Box>
          </Box>
        )}
    </Box>
  );

  return (
    <Box paddingX={2} marginTop={4} justifyContent="center" display="flex">
      <Box maxWidth={850} width="100%">
        {tabNav}
      </Box>
    </Box>
  );
}

export default compose(
  withViewer,
  withRouter,
  withRequestContext,
)(ProfileTabBar);
